/**
 * Pause overlay (sc-2819).
 *
 * Shown when the player presses Escape mid-cartridge. Pauses the runtime
 * loop, dims the cartridge's last rendered frame, and presents a menu:
 *
 *   - RESUME (continue the cartridge from where it paused)
 *   - CONTROLS listing (cartridge-specific, from GameModule.controls)
 *   - RETURN TO SELECTOR (exit cartridge back to the game picker)
 *
 * The overlay is canvas-rendered, not DOM, so it lives inside the
 * bezel/cabinet aesthetic. Owns its own rAF loop for blink animation
 * and input polling.
 *
 * Input contract: the overlay polls the same InputSource as the
 * cartridge. Up/Down to move focus, Z/Enter to confirm. Escape to
 * resume (matches the open binding).
 */

import type { ControlBinding, GameInstance, GameModule } from '@arcraz/arcadia-types';
import { getCabinetAudioBus } from './context.js';
import type { InputSource } from './loop.js';

const WIDTH = 480;
const HEIGHT = 270;

const SCRIM = 'rgba(4, 6, 13, 0.55)';
const PANEL_BG = 'rgba(10, 4, 24, 0.78)';
const PANEL_BORDER = '#ffea00';
const TITLE = '#ffea00';
const ROW_DIM = 'rgba(255, 234, 0, 0.4)';
const ROW_FOCUSED = '#ffea00';
const CONTROLS_LABEL = '#7af3c8';
const CONTROLS_KEY = '#00d9ff';
const CONTROLS_ACTION = '#ffffff';
const HINT = '#7af3c8';

const FALLBACK_CONTROLS: readonly ControlBinding[] = [
  { keys: 'ARROWS', action: 'MOVE' },
  { keys: 'Z', action: 'ACTION' },
];

export type PauseChoice = 'resume' | 'quit';

interface MenuItem {
  readonly label: string;
  readonly choice: PauseChoice;
}

const MENU: readonly MenuItem[] = [
  { label: 'RESUME', choice: 'resume' },
  { label: 'RETURN TO SELECTOR', choice: 'quit' },
];

/**
 * Run the pause overlay until the player picks an action.
 *
 * Each tick: re-renders the cartridge's frozen frame (cheap — game state
 * doesn't change while paused), then paints the translucent scrim +
 * panel + menu on top. The cartridge frame visibly shows through so the
 * player can see what they paused, while still being clearly subordinate
 * to the menu UI.
 *
 * Caller is responsible for pausing the cartridge's runtime loop AND
 * calling `instance.pause()` BEFORE calling this — otherwise the
 * cartridge's update() will keep firing under the overlay.
 */
export function runPauseOverlay(
  ctx2d: CanvasRenderingContext2D,
  input: InputSource,
  module_: GameModule,
  instance: GameInstance
): Promise<PauseChoice> {
  let focusIndex = 0;
  let blink = 0;
  const audio = getCabinetAudioBus();

  return new Promise<PauseChoice>((resolve) => {
    // M-key mute toggle lives at the cabinet level (main.ts) so it works
    // during gameplay too; we just read getMuted() to redraw status here.

    const tick = (): void => {
      const state = input.snapshot();

      if (state.justPressed.has('up') && focusIndex > 0) {
        focusIndex -= 1;
      } else if (state.justPressed.has('down') && focusIndex < MENU.length - 1) {
        focusIndex += 1;
      }

      // Z/Enter confirms the focused choice. Escape (mapped to a synthetic
      // key — see cabinet wiring) maps to RESUME instantly so the player
      // can dismiss the overlay with the same key they used to open it.
      if (state.justPressed.has('start') || state.justPressed.has('fireA')) {
        const item = MENU[focusIndex] as MenuItem;
        resolve(item.choice);
        return;
      }

      // The cabinet-level Escape listener (see main.ts) toggles a
      // synthetic justPressed on `coin` for "request pause toggle" — when
      // already paused, that means RESUME.
      if (state.justPressed.has('coin')) {
        resolve('resume');
        return;
      }

      // Re-render the cartridge frame each tick so the translucent scrim
      // doesn't cumulatively stack opacity. Game state is frozen (caller
      // already called handle.pause() + instance.pause()) so this is just
      // re-painting the same image.
      instance.render(ctx2d);
      blink = (blink + 1) % 60;
      render(ctx2d, module_, focusIndex, blink, audio.getMuted());
      requestAnimationFrame(tick);
    };

    tick();
  });
}

function render(
  g: CanvasRenderingContext2D,
  module_: GameModule,
  focusIndex: number,
  blink: number,
  muted: boolean
): void {
  // Dim the cartridge's last frame (already on the canvas — we don't
  // re-clear). Then paint the menu panel centered.
  g.fillStyle = SCRIM;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  const panelW = 320;
  const panelH = 200;
  const panelX = WIDTH / 2 - panelW / 2;
  const panelY = HEIGHT / 2 - panelH / 2;

  g.fillStyle = PANEL_BG;
  g.fillRect(panelX, panelY, panelW, panelH);
  g.strokeStyle = PANEL_BORDER;
  g.lineWidth = 2;
  g.strokeRect(panelX + 0.5, panelY + 0.5, panelW - 1, panelH - 1);

  // PAUSED heading.
  g.textAlign = 'center';
  g.textBaseline = 'top';
  g.fillStyle = TITLE;
  g.font = 'bold 16px ui-monospace, monospace';
  g.fillText('PAUSED', WIDTH / 2, panelY + 12);

  // Cartridge title underneath as subtitle.
  g.font = '8px ui-monospace, monospace';
  g.fillStyle = CONTROLS_LABEL;
  g.fillText(module_.title, WIDTH / 2, panelY + 32);

  // Controls listing. Cabinet appends a synthetic M binding for mute
  // since it's cabinet-level (every cartridge inherits it) — players
  // reflexively look for "mute" in the controls panel, not under the
  // menu.
  const controls = module_.controls ?? FALLBACK_CONTROLS;
  g.textAlign = 'left';
  g.font = '8px ui-monospace, monospace';
  let cy = panelY + 50;
  g.fillStyle = CONTROLS_LABEL;
  g.fillText('CONTROLS', panelX + 18, cy);
  cy += 10;
  for (const c of controls) {
    g.fillStyle = CONTROLS_KEY;
    g.fillText(c.keys, panelX + 18, cy);
    g.fillStyle = CONTROLS_ACTION;
    g.textAlign = 'right';
    g.fillText(c.action, panelX + panelW - 18, cy);
    g.textAlign = 'left';
    cy += 10;
  }
  // Cabinet-level entries: appear in every cartridge's controls panel.
  g.fillStyle = CONTROLS_KEY;
  g.fillText('M', panelX + 18, cy);
  g.fillStyle = muted ? '#ff8080' : CONTROLS_ACTION;
  g.textAlign = 'right';
  g.fillText(`MUTE AUDIO (${muted ? 'OFF' : 'ON'})`, panelX + panelW - 18, cy);
  g.textAlign = 'left';

  // Menu items, drawn near the bottom of the panel.
  g.textAlign = 'center';
  g.font = 'bold 11px ui-monospace, monospace';
  const menuStartY = panelY + panelH - 50;
  for (let i = 0; i < MENU.length; i++) {
    const item = MENU[i] as MenuItem;
    const focused = i === focusIndex;
    const my = menuStartY + i * 16;

    // Focus chevron.
    if (focused && blink < 40) {
      g.textAlign = 'left';
      g.fillStyle = ROW_FOCUSED;
      g.fillText('>', panelX + 30, my);
      g.textAlign = 'center';
    }

    g.fillStyle = focused ? ROW_FOCUSED : ROW_DIM;
    g.fillText(item.label, WIDTH / 2, my);
  }

  // Hint below the panel — keys for navigating this overlay.
  g.font = '8px ui-monospace, monospace';
  g.textBaseline = 'top';
  g.fillStyle = HINT;
  g.fillText('ESC — RESUME    Z — CONFIRM', WIDTH / 2, panelY + panelH + 8);

  g.textAlign = 'left';
  g.textBaseline = 'top';
}
